import { isSeoUserAgent } from '@bcf-vanilla-shared-ts-v1/seo/is-seo-user-agent';
import { provideDiTokenValue } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { GTM_ID } from '@bcf2-v1-configurators-tokens/gtm/token';

interface WindowWithGtm extends Window {
  dataLayer?: unknown[];
  gtag?: GtagFunction;
  isGtmLazyLoaded?: boolean;
}

type GtagFunction = (...args: unknown[]) => void;

const gtmLazyLoadDelayMs: number = 10000;

export class GtmLoaderService {
  private _initialized: boolean = false;
  private _gtmInitialized: boolean = false;

  constructor(
    private _gtmId: string | undefined,
    private _window: Window,
    private _document: Document,
    private _navigator: Navigator
  ) {}

  public init(): void {
    if (this._initialized || __RENDER_MODE__ !== 'browser') {
      return;
    }
    this._initialized = true;

    if (isSeoUserAgent(this._navigator.userAgent)) {
      return;
    }
    if (!this._gtmId) {
      return;
    }

    const gtmId: string = this._gtmId;

    if (this._window.location.search.includes('gtm_debug')) {
      this._initGtm(gtmId);
      return;
    }

    this._window.addEventListener('click', () => this._initGtm(gtmId), { once: true });
    this._window.addEventListener('touchstart', () => this._initGtm(gtmId), { once: true });
    this._window.addEventListener('scroll', () => this._initGtm(gtmId), { once: true });

    if (this._window.location.hash !== '') {
      this._initGtm(gtmId);
    }

    this._window.setTimeout(() => {
      this._initGtm(gtmId);
    }, gtmLazyLoadDelayMs);
  }

  private _initGtm(gtmId: string): void {
    if (this._gtmInitialized) {
      return;
    }
    this._gtmInitialized = true;

    const windowRef: WindowWithGtm = this._window as WindowWithGtm;
    windowRef.dataLayer = windowRef.dataLayer ?? [];
    windowRef.gtag = (...args: unknown[]): void => {
      windowRef.dataLayer!.push(args);
    };
    windowRef.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      personalization_storage: 'denied'
    });
    windowRef.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const firstScript: HTMLScriptElement | undefined = this._document.getElementsByTagName('script')[0];
    const gtmScript: HTMLScriptElement = this._document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    gtmScript.onload = (): void => {
      windowRef.isGtmLazyLoaded = true;
      windowRef.dispatchEvent(new CustomEvent('gtmLoaded'));
    };
    firstScript?.parentNode?.insertBefore(gtmScript, firstScript);
  }
}

export function provideGtmLoaderService(): GtmLoaderService {
  return provideSingleton(
    GtmLoaderService,
    () => new GtmLoaderService(provideDiTokenValue(GTM_ID), window, document, window.navigator)
  );
}
